// client/src/components/modals/StatsModal.js
import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode.react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// Global cache object to store successful responses
const statsCache = {};

// Global object to track active requests and prevent duplicates
const activeRequests = {};

function StatsModal({ isOpen, onClose, item, itemType }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Single useEffect to handle all data fetching
  useEffect(() => {
    // Clean up function to run on unmount or dependency change
    let isActive = true;
    let timeoutId = null;

    const cleanUp = () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    // If modal is closed or no item, reset and return
    if (!isOpen || !item || !item._id) {
      if (!isOpen) {
        setStats(null);
        setLoading(true);
        setError(null);
      } else if (item && item._id) {
        setLoading(false);
      }
      return cleanUp;
    }

    // Start loading
    setLoading(true);
    setError(null);
    
    // Generate cache key for this data
    const cacheKey = `${itemType}-${item._id}`;
    
    // First check cache
    if (statsCache[cacheKey]) {
      console.log('Using cached stats for', cacheKey);
      setStats(statsCache[cacheKey]);
      setLoading(false);
      return cleanUp;
    }
    
    // If there's already an active request for this data, don't make another one
    if (activeRequests[cacheKey]) {
      console.log('Request already in progress for', cacheKey);
      
      // Set up polling to check when data becomes available
      const checkInterval = setInterval(() => {
        if (statsCache[cacheKey] && isActive) {
          console.log('Data now available in cache');
          setStats(statsCache[cacheKey]);
          setLoading(false);
          clearInterval(checkInterval);
        }
      }, 500);
      
      // Clean up interval on unmount
      return () => {
        cleanUp();
        clearInterval(checkInterval);
      };
    }
    
    // Mark this request as active
    activeRequests[cacheKey] = true;
    
    // Set timeout for fallback data
    timeoutId = setTimeout(() => {
      if (isActive) {
        console.log('Request timed out, using fallback data');
        const fallbackStats = {
          total: item.visits || 0,
          devices: { desktop: item.visits || 0, mobile: 0, tablet: 0, other: 0 },
          locations: [],
          timeSeries: [{ date: new Date().toISOString().split('T')[0], count: item.visits || 0 }]
        };
        
        statsCache[cacheKey] = fallbackStats;
        setStats(fallbackStats);
        setLoading(false);
        delete activeRequests[cacheKey];
      }
    }, 5000);
    
    // Fetch data from the API
    const fetchData = async () => {
      try {
        const endpoint = itemType === 'qr' 
          ? `/api/qrcodes/${item._id}/stats` 
          : `/api/linkshorts/${item._id}/stats`;
        
        console.log('Fetching stats from:', endpoint);
        
        const response = await fetch(endpoint, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        // If component unmounted or dependencies changed, abort
        if (!isActive) return;
        
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Received stats data:', data);
        
        // If component unmounted or dependencies changed, abort
        if (!isActive) return;
        
        // Cache the successful response
        statsCache[cacheKey] = data;
        setStats(data);
        setLoading(false);
        clearTimeout(timeoutId);
      } catch (err) {
        console.error('Error fetching stats:', err);
        
        if (isActive) {
          setError(err.message);
          setLoading(false);
        }
      } finally {
        delete activeRequests[cacheKey];
      }
    };
    
    // Start the fetch
    fetchData();
    
    // Return cleanup function
    return cleanUp;
  }, [isOpen, item, itemType]);

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={e => {
      // Close when clicking outside the modal content
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Modal header */}
        <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold">
            {itemType === 'qr' ? 'QR Stats' : 'Link Stats'}: {itemType === 'qr' ? item.qr_name : item.shortId}
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Modal content */}
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">Loading stats...</div>
          )}
          
          {!loading && stats && (
            <div className="space-y-6">
              {error && (
                <div className="bg-yellow-50 border border-yellow-100 p-3 rounded text-yellow-700 text-sm">
                  Note: Using estimated data due to server connection issues.
                </div>
              )}
              
              {/* Basic info */}
              <div className="flex flex-col md:flex-row md:space-x-6">
                <div className="md:w-1/3 mb-4 md:mb-0">
                  {itemType === 'qr' && (
                    <div className="bg-gray-100 p-4 rounded-lg flex flex-col items-center">
                      <div className="w-32 h-32 mb-2">
                        <QRCode 
                          value={`${window.location.origin}/${item.shortId}`}
                          size={128}
                          fgColor={item.foregroundColor || '#000000'}
                          bgColor={item.backgroundColor || '#FFFFFF'}
                          level="H"
                          renderAs="svg"
                          id="qr-preview"
                        />
                      </div>
                      <button 
                        className="bg-blue-500 text-white py-1 px-3 rounded text-sm hover:bg-blue-600"
                      >
                        Download
                      </button>
                    </div>
                  )}
                  
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p><span className="font-medium">Redirects to:</span> {item.targetUrl}</p>
                    <p className="mt-2"><span className="font-medium">Total {itemType === 'qr' ? 'Scans' : 'Clicks'}:</span> {stats?.total || 0}</p>
                    <p className="mt-2"><span className="font-medium">Status:</span> {item.isActive ? 'Active' : 'Inactive'}</p>
                    {item.createdAt && (
                      <p className="mt-2"><span className="font-medium">Created:</span> {new Date(item.createdAt).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
                
                <div className="md:w-2/3">
                  {/* Time series chart */}
                  {stats?.timeSeries && stats.timeSeries.length > 0 && (
                    <div className="bg-gray-100 p-4 rounded-lg mb-4">
                      <h3 className="font-medium mb-2">Activity Over Time</h3>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={stats.timeSeries}>
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="count" stroke="#3B82F6" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                  
                  {/* Device breakdown */}
                  <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    <h3 className="font-medium mb-2">Device Breakdown</h3>
                    <div className="flex space-x-4">
                      <div className="flex-1 text-center">
                        <div className="text-xl font-bold">{stats?.devices?.mobile || 0}</div>
                        <div>Mobile</div>
                      </div>
                      <div className="flex-1 text-center">
                        <div className="text-xl font-bold">{stats?.devices?.desktop || 0}</div>
                        <div>Desktop</div>
                      </div>
                      <div className="flex-1 text-center">
                        <div className="text-xl font-bold">{stats?.devices?.tablet || 0}</div>
                        <div>Tablet</div>
                      </div>
                      <div className="flex-1 text-center">
                        <div className="text-xl font-bold">{stats?.devices?.other || 0}</div>
                        <div>Other</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Locations */}
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Top Locations</h3>
                    {stats?.locations && stats.locations.length > 0 ? (
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-gray-200 sticky top-0">
                            <tr>
                              <th className="text-left py-2 px-3">Location</th>
                              <th className="text-right py-2 px-3">Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.locations.map((location, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="py-2 px-3">
                                  {location.city ? `${location.city}${location.region ? `, ${location.region}` : ''}${location.country ? ` (${location.country})` : ''}` : 
                                   `${location.region || ''}${location.region && location.country ? ', ' : ''}${location.country ? `${location.country}` : 'Unknown'}`}
                                </td>
                                <td className="text-right py-2 px-3">{location.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">No location data available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {!loading && !stats && (
            <div className="text-center py-8 text-red-500">
              No stats data available. Please try again later.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StatsModal;